/** Формулы дашборда «Заказы по артикулам 1С» — дословный порт клиентской логики
 * оригинала (orders-dashboard-hosting/work/build-orders-dashboard-html.js).
 * Ссылки на строки билд-скрипта у каждой функции. Менять формулы можно только
 * синхронно с оригиналом — иначе числа разойдутся с привычным дашбордом.
 *
 * Отличие входа: вместо дневных строк (data.daily) сервер отдаёт помесячные ряды
 * по строкам артикул×id_1c×МП + дневные скаляры (MTD-пара, last30/last90, спарк-корзины).
 * Все дневные суммы оригинала линейны по МП и id_1c, поэтому клиент восстанавливает
 * их сложением при склейке вкладки.
 */
import type {
  MpCode,
  OrdersMatrixReport,
  OrdersMatrixRow,
} from "@/api/orders";

export type Tab = "all" | "wb" | "ozon" | "ym";

/** Строка вкладки: склейка серверных строк по keyFor = article::id_1c (порт стр. 3539, 3613-3707). */
export interface ViewRow {
  key: string;
  article: string;
  id_1c: string;
  code_1c: string;
  name: string;
  brand: string;
  qty: number[];
  total: number[];
  cost: number[];
  margin: number[];
  costedQty: number[];
  qtySum: number;
  totalSum: number;
  costSum: number;
  marginSum: number;
  costedQtySum: number;
  mps: Record<MpCode, number>;
  /** last30 уровня строки (сумма по склеенным серверным строкам) — для KPI
   * «Продажи 30 дней»/«Активных» (порт rows30/keyFor, стр. 4138-4140).
   * В rowMetrics.last30 идёт артикул-уровень — это разные величины. */
  rowLast30: number;
  firstSale: string | null;
  lastSale: string | null;
  spark: number[];
  stockMp: number;
  stock1c: number;
  stockTotal: number;
  abcxyz: string;
}

/** Артикул-уровневые дневные суммы вкладки (порт activeSourceSeries/sumSourceOrders,
 * стр. 3838-3884: серия ключуется артикулом и НЕ фильтруется поиском). */
export interface ArticleScalars {
  currentMtd: number;
  previousMtd: number;
  last30: number;
  last90: number;
  qtyRange: number; // сумма за выбранный диапазон — для selectedAvg в stockDailyDemand
}

export interface ViewContext {
  months: string[];
  dateFrom: string;
  dateTo: string;
  bucketCount: number;
  articleScalars: Map<string, ArticleScalars>;
}

export interface RowMetrics {
  current: number;
  previous: number;
  currentMtd: number;
  delta: number;
  previousMtd: number;
  deltaMtd: number;
  last30: number;
  dailyDemand: number;
  stockDays: number | null;
  stockDaysSort: number;
}

export type QuickFilter =
  | ""
  | "current"
  | "no-current"
  | "stock"
  | "risk"
  | "active"
  | "stock-no-sales"
  | "sales-no-stock"
  | "drop"
  | "growth";

/** Порт matchesSearch (стр. 3540-3544). */
export function matchesSearch(
  row: { article: string; name: string; brand: string; code_1c: string; id_1c: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.article, row.name, row.brand, row.code_1c, row.id_1c]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

/** Порт prevMonth (стр. 3766-3771). */
export function prevMonthKey(month: string): string {
  let y = Number(month.slice(0, 4));
  let m = Number(month.slice(5, 7)) - 1;
  if (m === 0) {
    m = 12;
    y -= 1;
  }
  return `${y}-${String(m).padStart(2, "0")}`;
}

/** Порт daysBetween (стр. 3885-3889): включительно, минимум 1. */
export function daysBetween(from: string, to: string): number {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
}

function rowMatchesTab(row: OrdersMatrixRow, tab: Tab): boolean {
  return tab === "all" || row.mp_code === tab;
}

const EMPTY_MPS = (): Record<MpCode, number> => ({ wb: 0, ozon: 0, ym: 0 });

function sum(xs: number[]): number {
  return xs.reduce((s, v) => s + v, 0);
}

function addInto(dst: number[], src: number[]): void {
  for (let i = 0; i < dst.length && i < src.length; i += 1) dst[i] = (dst[i] ?? 0) + (src[i] ?? 0);
}

/** Склейка вкладки + остатки + ABC/XYZ — порт articleRows() (стр. 3613-3707)
 * и stockIndex() (стр. 3548-3585). Возвращает строки в исходной сортировке
 * оригинала (qty desc, артикул asc — от неё зависит ABC). */
export function buildView(
  report: OrdersMatrixReport,
  tab: Tab,
  search: string,
): { rows: ViewRow[]; ctx: ViewContext } {
  const months = report.months;
  const tabRows = report.rows.filter((r) => rowMatchesTab(r, tab));

  // Артикул-уровневые скаляры вкладки: БЕЗ поиска (порт activeSourceSeries).
  const articleScalars = new Map<string, ArticleScalars>();
  for (const r of tabRows) {
    const s = articleScalars.get(r.article) ?? {
      currentMtd: 0, previousMtd: 0, last30: 0, last90: 0, qtyRange: 0,
    };
    s.currentMtd += r.current_mtd;
    s.previousMtd += r.previous_mtd;
    s.last30 += r.last30;
    s.last90 += r.last90;
    s.qtyRange += sum(r.qty);
    articleScalars.set(r.article, s);
  }

  const searchRows = tabRows.filter((r) =>
    matchesSearch(
      {
        article: r.article, name: r.name ?? "", brand: r.brand ?? "",
        code_1c: r.code_1c ?? "", id_1c: r.id_1c ?? "",
      },
      search,
    ),
  );

  // Склейка по keyFor = article::id_1c (порт стр. 3539, 3617-3656).
  const map = new Map<string, ViewRow>();
  for (const r of searchRows) {
    const key = `${r.article}::${r.id_1c ?? ""}`;
    let item = map.get(key);
    if (!item) {
      item = {
        key,
        article: r.article,
        id_1c: r.id_1c ?? "",
        code_1c: r.code_1c ?? "",
        name: r.name ?? r.article,
        brand: r.brand ?? "",
        qty: months.map(() => 0),
        total: months.map(() => 0),
        cost: months.map(() => 0),
        margin: months.map(() => 0),
        costedQty: months.map(() => 0),
        qtySum: 0, totalSum: 0, costSum: 0, marginSum: 0, costedQtySum: 0,
        mps: EMPTY_MPS(),
        rowLast30: 0,
        firstSale: null,
        lastSale: null,
        spark: Array.from({ length: report.bucket_count }, () => 0),
        stockMp: 0, stock1c: 0, stockTotal: 0,
        abcxyz: "",
      };
      map.set(key, item);
    }
    addInto(item.qty, r.qty);
    addInto(item.total, r.total);
    addInto(item.cost, r.cost);
    addInto(item.margin, r.margin);
    addInto(item.costedQty, r.costed_qty);
    addInto(item.spark, r.spark);
    const rowQty = sum(r.qty);
    item.qtySum += rowQty;
    item.totalSum += sum(r.total);
    item.costSum += sum(r.cost);
    item.marginSum += sum(r.margin);
    item.costedQtySum += sum(r.costed_qty);
    item.mps[r.mp_code] += rowQty;
    item.rowLast30 += r.last30;
    if (r.first_sale && (!item.firstSale || r.first_sale < item.firstSale)) {
      item.firstSale = r.first_sale;
    }
    if (r.last_sale && (!item.lastSale || r.last_sale > item.lastSale)) {
      item.lastSale = r.last_sale;
    }
  }

  // «Остаток без продаж»: артикулы из stocks, которых нет в строках (порт 3657-3686).
  for (const s of report.stocks) {
    if (!s.article) continue;
    if (s.mp_code && tab !== "all" && s.mp_code !== tab) continue;
    if (!matchesSearch(
      { article: s.article, name: s.article, brand: "", code_1c: "", id_1c: "" },
      search,
    )) continue;
    const exists = [...map.values()].some((item) => item.article === s.article);
    if (exists) continue;
    const key = `${s.article}::${s.article}`;
    if (map.has(key)) continue;
    map.set(key, {
      key,
      article: s.article,
      id_1c: "",
      code_1c: "",
      name: s.article,
      brand: "",
      qty: months.map(() => 0),
      total: months.map(() => 0),
      cost: months.map(() => 0),
      margin: months.map(() => 0),
      costedQty: months.map(() => 0),
      qtySum: 0, totalSum: 0, costSum: 0, marginSum: 0, costedQtySum: 0,
      mps: EMPTY_MPS(),
      rowLast30: 0,
      firstSale: null,
      lastSale: null,
      spark: Array.from({ length: report.bucket_count }, () => 0),
      stockMp: 0, stock1c: 0, stockTotal: 0,
      abcxyz: "",
    });
  }

  // stockIndex: доля продаж артикула по МП — по ВСЕМ МП независимо от вкладки,
  // с поиском, как в оригинале (стр. 3550-3557: activeDailyRows без tabMatches).
  const shareRows = report.rows.filter((r) =>
    matchesSearch(
      {
        article: r.article, name: r.name ?? "", brand: r.brand ?? "",
        code_1c: r.code_1c ?? "", id_1c: r.id_1c ?? "",
      },
      search,
    ),
  );
  const salesShare = new Map<string, Record<MpCode, number> & { total: number }>();
  for (const r of shareRows) {
    const share = salesShare.get(r.article) ?? { total: 0, ...EMPTY_MPS() };
    const rowQty = sum(r.qty);
    share.total += rowQty;
    share[r.mp_code] += rowQty;
    salesShare.set(r.article, share);
  }
  const stockByArticle = new Map<string, { stockMp: number; stock1cFull: number }>();
  for (const s of report.stocks) {
    if (!s.article) continue;
    if (s.mp_code && tab !== "all" && s.mp_code !== tab) continue;
    const item = stockByArticle.get(s.article) ?? { stockMp: 0, stock1cFull: 0 };
    if (s.mp_code) item.stockMp += s.stock_mp;
    else item.stock1cFull = Math.max(item.stock1cFull, s.stock_1c);
    stockByArticle.set(s.article, item);
  }

  // Сортировка + ABC/XYZ + привязка остатков (порт 3687-3705).
  const rows = [...map.values()].sort(
    (a, b) => b.qtySum - a.qtySum || a.article.localeCompare(b.article, "ru"),
  );
  let cumulative = 0;
  const totalQty = rows.reduce((s, row) => s + row.qtySum, 0) || 1;
  for (const row of rows) {
    cumulative += row.qtySum;
    const share = cumulative / totalQty;
    const abc = share <= 0.8 ? "A" : share <= 0.95 ? "B" : "C";
    // Object.values(row.months) оригинала = значения месяцев, где были продажи
    const vals = row.qty.filter((v) => v > 0);
    const avg = vals.reduce((s, v) => s + v, 0) / Math.max(1, vals.length);
    const variance =
      vals.reduce((s, v) => s + (v - avg) ** 2, 0) / Math.max(1, vals.length);
    const cv = avg ? Math.sqrt(variance) / avg : 0;
    const xyz = cv <= 0.25 ? "X" : cv <= 0.5 ? "Y" : "Z";
    row.abcxyz = abc + xyz;

    const stock = stockByArticle.get(row.article) ?? { stockMp: 0, stock1cFull: 0 };
    row.stockMp = stock.stockMp;
    if (tab === "all") {
      row.stock1c = stock.stock1cFull;
    } else {
      // На вкладке МП остаток 1С пропорционален доле продаж этого МП (стр. 3576-3584)
      const share = salesShare.get(row.article);
      const mpShare = share && share.total > 0 ? share[tab] / share.total : 0;
      row.stock1c = Math.round(stock.stock1cFull * mpShare);
    }
    row.stockTotal = row.stockMp + row.stock1c;
  }

  return {
    rows,
    ctx: {
      months,
      dateFrom: report.date_from,
      dateTo: report.date_to,
      bucketCount: report.bucket_count,
      articleScalars,
    },
  };
}

/** Порт stockDailyDemand (стр. 3890-3897): max(средняя за период, за 30 дней, 0.7×за 90). */
export function stockDailyDemand(row: ViewRow, ctx: ViewContext): number {
  const s = ctx.articleScalars.get(row.article);
  if (!s) return 0;
  const selectedDays = daysBetween(ctx.dateFrom, ctx.dateTo);
  const selectedAvg = s.qtyRange / selectedDays;
  const last30Avg = s.last30 / 30;
  const last90Avg = (s.last90 / 90) * 0.7;
  return Math.max(selectedAvg, last30Avg, last90Avg);
}

/** Порт rowMetrics (стр. 3898-3925). MTD/last30 — артикул-уровень, как sumSourceOrders. */
export function rowMetrics(row: ViewRow, ctx: ViewContext): RowMetrics {
  const months = ctx.months;
  const currentMonth = months[months.length - 1] ?? "";
  const previousMonth = prevMonthKey(currentMonth);
  const prevIdx = months.indexOf(previousMonth);
  const current = row.qty[months.length - 1] ?? 0;
  const previous = (prevIdx >= 0 ? row.qty[prevIdx] : 0) ?? 0;
  const s = ctx.articleScalars.get(row.article) ?? {
    currentMtd: 0, previousMtd: 0, last30: 0, last90: 0, qtyRange: 0,
  };
  const dailyDemand = stockDailyDemand(row, ctx);
  const stockDays =
    dailyDemand > 0
      ? Math.round(row.stockTotal / dailyDemand)
      : row.stockTotal > 0
        ? null
        : 0;
  return {
    current,
    previous,
    currentMtd: s.currentMtd,
    delta: current - previous,
    previousMtd: s.previousMtd,
    deltaMtd: s.currentMtd - s.previousMtd,
    last30: s.last30,
    dailyDemand,
    stockDays,
    stockDaysSort: stockDays === null ? 999999 : stockDays,
  };
}

/** Минимальный вид атрибутов артикула для фильтров (полный тип — в api/orders-planning). */
export interface ArticleAttrsLike {
  product_type: string;
  characteristic: string;
}

/** Порт applyViewFilters (стр. 3926-3948), включая фильтры справочника. */
export function applyViewFilters(
  rows: ViewRow[],
  ctx: ViewContext,
  opts: {
    abc?: string;
    brand?: string;
    quick?: QuickFilter;
    group?: string;
    characteristic?: string;
    attrs?: Map<string, ArticleAttrsLike>;
  },
): ViewRow[] {
  const { abc = "", brand = "", quick = "", group = "", characteristic = "", attrs } = opts;
  return rows.filter((row) => {
    if (abc && row.abcxyz !== abc) return false;
    if (brand && row.brand !== brand) return false;
    if (group && (attrs?.get(row.article)?.product_type ?? "") !== group) return false;
    if (characteristic && (attrs?.get(row.article)?.characteristic ?? "") !== characteristic) {
      return false;
    }
    if (!quick) return true;
    const m = rowMetrics(row, ctx);
    if (quick === "current" && m.current <= 0) return false;
    if (quick === "no-current" && m.current > 0) return false;
    if (quick === "stock" && row.stockTotal <= 0) return false;
    if (
      quick === "risk" &&
      !(
        row.stockTotal <= 0 ||
        m.stockDays === null ||
        (m.stockDays > 0 && m.stockDays <= 21) ||
        m.deltaMtd < 0
      )
    )
      return false;
    if (quick === "active" && m.last30 <= 0) return false;
    if (quick === "stock-no-sales" && !(row.stockTotal > 0 && m.last30 === 0)) return false;
    if (quick === "sales-no-stock" && !(m.last30 > 0 && row.stockMp <= 0)) return false;
    if (quick === "drop" && m.deltaMtd >= 0) return false;
    if (quick === "growth" && m.deltaMtd <= 0) return false;
    return true;
  });
}

export interface Recommendation {
  cls: "red" | "yellow" | "green" | "blue";
  title: string;
  note: string;
}

/** Порт recommendation (стр. 5287-5296). */
export function recommendation(row: ViewRow, m: RowMetrics): Recommendation {
  if (row.stockTotal <= 0 && m.last30 > 0)
    return { cls: "red", title: "Довезти", note: "Есть продажи, но совокупный остаток нулевой." };
  if (row.stockMp <= 0 && m.last30 > 0)
    return { cls: "red", title: "Довезти", note: "Продажи есть, на маркетплейсе остаток не виден." };
  if (m.stockDays === null && row.stockTotal > 0)
    return {
      cls: "yellow",
      title: "Нет продаж при остатке",
      note: "Остаток есть, но спрос не рассчитан: нет продаж в выбранном/30/90-дневном окне.",
    };
  if (m.stockDays !== null && m.stockDays > 0 && m.stockDays <= 21)
    return { cls: "yellow", title: "Довезти", note: "Запаса меньше трёх недель при текущем темпе." };
  if (row.stockTotal > 0 && m.last30 === 0)
    return {
      cls: "yellow",
      title: "Нет продаж при остатке",
      note: "Стоп довоза: остаток есть, продаж за 30 дней нет.",
    };
  if (m.deltaMtd < 0)
    return {
      cls: "red",
      title: "Проверить карточку",
      note: "MTD ниже прошлого месяца, стоит проверить карточку и видимость товара.",
    };
  if (m.deltaMtd > 0) return { cls: "green", title: "Рост", note: "Темп лучше прошлого месяца." };
  return { cls: "blue", title: "Норма", note: "Критичных сигналов по текущему срезу нет." };
}

/** Порт heatClass (стр. 5277-5283). */
export function heatClass(value: number, max: number): "" | "hot1" | "hot2" | "hot3" {
  if (!value) return "";
  const ratio = value / Math.max(1, max);
  if (ratio > 0.65) return "hot3";
  if (ratio > 0.35) return "hot2";
  return "hot1";
}

export interface StockDayRange {
  from: number;
  to: number;
  color: string;
  label: string;
}

/** Порт defaultStockDaySettings (стр. 4886-4896); пользовательская настройка отложена
 * вместе со справочником. */
export const STOCK_DAY_RANGES: StockDayRange[] = [
  { from: 0, to: 14, color: "#ff6b75", label: "Критично" },
  { from: 15, to: 30, color: "#f1c453", label: "Контроль" },
  { from: 31, to: 60, color: "#58d69b", label: "Норма" },
  { from: 61, to: 9999, color: "#5ec8f2", label: "Избыток" },
];
export const NO_SALES_COLOR = "#7f90a7";

/** Порт stockDayRange (стр. 5058-5062). */
export function stockDayRange(value: number | null): { color: string; label: string } {
  if (value === null || value === undefined) return { color: NO_SALES_COLOR, label: "нет продаж" };
  return (
    STOCK_DAY_RANGES.find((r) => value >= r.from && value <= r.to) ??
    STOCK_DAY_RANGES[STOCK_DAY_RANGES.length - 1] ??
    { color: NO_SALES_COLOR, label: "" }
  );
}

export interface WarehouseSignal {
  daily: number;
  stockDays: number | null;
  range: { color: string; label: string };
  action: string;
  cls: "danger" | "warn" | "idle" | "ok";
}

/** Порт warehouseSupplySignal (стр. 5063-5089): подсказка по складу.
 * daily — средние продажи склада за выбранный период (дней в диапазоне). */
export function warehouseSupplySignal(
  item: { sales: number; stock: number },
  selectedDays: number,
): WarehouseSignal {
  const daily = item.sales > 0 ? item.sales / selectedDays : 0;
  const stockDays = daily > 0 ? Math.round(item.stock / daily) : null;
  const range = stockDayRange(stockDays);
  const ranges = STOCK_DAY_RANGES;
  let action = "Норма";
  let cls: WarehouseSignal["cls"] = "ok";
  if (item.stock <= 0 && item.sales > 0) {
    action = "Срочно допоставить";
    cls = "danger";
  } else if (stockDays === null && item.stock > 0) {
    action = "Остаток без продаж";
    cls = "idle";
  } else if (stockDays !== null) {
    if (stockDays <= 30) {
      action = stockDays <= 14 ? "Срочно допоставить" : "Допоставить в ближайшие 30 дней";
      cls = stockDays <= 14 ? "danger" : "warn";
    } else if (ranges[0] && stockDays <= ranges[0].to) {
      action = "Допоставить";
      cls = "danger";
    } else if (ranges[1] && stockDays <= ranges[1].to) {
      action = "Контроль";
      cls = "warn";
    }
  }
  return { daily, stockDays, range, action, cls };
}

/** Порт marginPct (стр. 3742-3744): маржинальность в % с одним знаком. */
export function marginPct(margin: number, amount: number): number {
  return amount ? Math.round((margin / amount) * 1000) / 10 : 0;
}

/** KPI вкладки — порт renderKpis (стр. 4127-4153). Всё в штуках. */
export interface TabKpis {
  currentMonth: string;
  previousMonth: string;
  current: number;
  previous: number;
  delta: number;
  qty30: number;
  totalQty: number;
  articles: number;
  active: number;
  unmappedQty: number;
}

export function computeKpis(
  rows: ViewRow[],
  ctx: ViewContext,
  report: OrdersMatrixReport,
  tab: Tab,
): TabKpis {
  const months = ctx.months;
  const lastIdx = months.length - 1;
  const prevIdx = months.length - 2;
  const current = rows.reduce((s, r) => s + (r.qty[lastIdx] ?? 0), 0);
  const previous = prevIdx >= 0 ? rows.reduce((s, r) => s + (r.qty[prevIdx] ?? 0), 0) : 0;
  // «Продажи 30 дней» и «Активных» — last30 уровня строки, окно от date_to (стр. 4134-4140)
  const qty30 = rows.reduce((s, r) => s + r.rowLast30, 0);
  const active = rows.filter((r) => r.rowLast30 > 0).length;
  const unmappedQty = report.unmapped
    .filter((u) => tab === "all" || u.mp_code === tab)
    .reduce((s, u) => s + u.qty, 0);
  return {
    currentMonth: months[lastIdx] ?? "",
    previousMonth: (prevIdx >= 0 ? months[prevIdx] : months[lastIdx]) ?? "",
    current,
    previous,
    delta: current - previous,
    qty30,
    totalQty: rows.reduce((s, r) => s + r.qtySum, 0),
    articles: rows.length,
    active,
    unmappedQty,
  };
}

/** Карточки качества — порт renderStatus (стр. 4168-4174). */
export interface QualityStats {
  unmappedMps: number;
  noName: number;
  stockNoSales: number;
  salesNoStock: number;
}

export function computeQuality(
  rows: ViewRow[],
  ctx: ViewContext,
  report: OrdersMatrixReport,
  tab: Tab,
): QualityStats {
  let noName = 0;
  let stockNoSales = 0;
  let salesNoStock = 0;
  for (const row of rows) {
    const m = rowMetrics(row, ctx);
    if (!row.name || row.name === row.article) noName += 1;
    if (row.stockTotal > 0 && m.last30 === 0) stockNoSales += 1;
    if (m.last30 > 0 && row.stockMp <= 0) salesNoStock += 1;
  }
  return {
    unmappedMps: report.unmapped.filter((u) => tab === "all" || u.mp_code === tab).length,
    noName,
    stockNoSales,
    salesNoStock,
  };
}
